import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  Settings,
  Monitor,
  Globe,
  Clock,
  Bell,
  BellOff,
  Mail,
  MessageSquare,
  Shield,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Palette,
  Calendar,
  Zap,
  Download,
  Users,
  Timer,
} from 'lucide-react';
import { Container } from '../styles/GlobalStyles';
import { theme } from '../styles/theme';
import { AnalysisAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import Particles from '../components/Particles';

// =============================================================================
// MOTION
// =============================================================================

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const tabSwap = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// =============================================================================
// PAGE SHELL
// =============================================================================

const SettingsContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[8]} 0 ${theme.spacing[16]};
  margin: 30px 30px 0;
  border: 1px solid ${theme.colors.border};
  border-radius: 20px;
  position: relative;
  /* clip (not hidden) — hidden would establish a scroll container and break
     position: sticky on the ActionBar at the bottom of the form card. */
  overflow: clip;

  @media (max-width: ${theme.breakpoints.md}) {
    margin: 20px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    margin: 0;
    border-radius: 0;
    border: none;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1080px;
  margin: 0 auto;
  padding: 0 ${theme.spacing[4]};
  animation: ${fadeUp} 0.45s ease-out both;
`;

// =============================================================================
// PAGE HEADER
// =============================================================================

const PageHeader = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[8]};
  padding-bottom: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.borderLight};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing[4]};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Crumb = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.textSecondary};
  margin: ${theme.spacing[1]} 0 0;
  max-width: 540px;
`;

const PreviewPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 8px ${theme.spacing[4]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.full};
  box-shadow: ${theme.shadows.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  white-space: nowrap;

  & .pulse {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 4px ${theme.colors.primary[500]}20;
    flex-shrink: 0;
  }

  & strong {
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
  }
`;

// =============================================================================
// SETTINGS CARD SHELL
// =============================================================================

const SettingsCard = styled.section`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.sm};
  position: relative;
`;

// Thin navy ribbon at the top of the card. Sets the "preview mode" tone so
// the disabled state below reads as intentional, not broken.
const PreviewRibbon = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border-radius: ${theme.borderRadius.xl} ${theme.borderRadius.xl} 0 0;
  background:
    radial-gradient(120% 80% at 100% 0%, rgba(255, 255, 255, 0.10) 0%, transparent 60%),
    linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[700]} 100%);
  color: rgba(255, 255, 255, 0.95);
  font-size: ${theme.typography.fontSize.sm};
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid ${theme.colors.primary[700]};

  /* Subtle grain to add tactile depth on the deep navy. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.04 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    opacity: 0.7;
    pointer-events: none;
    mix-blend-mode: screen;
  }

  & .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: ${theme.borderRadius.sm};
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    flex-shrink: 0;
    position: relative;
    z-index: 1;
  }

  & .copy {
    flex: 1;
    min-width: 0;
    position: relative;
    z-index: 1;

    & strong {
      color: #fff;
      font-weight: ${theme.typography.fontWeight.semibold};
    }
    & span {
      color: rgba(255, 255, 255, 0.7);
    }
  }

  & .badge {
    font-size: 10px;
    font-weight: ${theme.typography.fontWeight.bold};
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${theme.colors.primary[700]};
    background: #fff;
    padding: 4px ${theme.spacing[3]};
    border-radius: ${theme.borderRadius.full};
    box-shadow: 0 4px 12px -6px rgba(0, 0, 0, 0.4);
    position: relative;
    z-index: 1;
    flex-shrink: 0;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[3]} ${theme.spacing[5]};
    & .copy {
      font-size: ${theme.typography.fontSize.xs};
    }
  }
`;

// =============================================================================
// CARD BODY LAYOUT (sidebar nav + content)
// =============================================================================

const CardBody = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  align-items: stretch;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const SidebarNav = styled.nav`
  border-right: 1px solid ${theme.colors.borderLight};
  padding: ${theme.spacing[6]} ${theme.spacing[4]};
  background: ${theme.colors.backgroundSecondary};

  @media (max-width: ${theme.breakpoints.md}) {
    border-right: none;
    border-bottom: 1px solid ${theme.colors.borderLight};
    padding: ${theme.spacing[4]};
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;
  }
`;

const NavEyebrow = styled.div`
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textMuted};
  padding: 0 ${theme.spacing[3]} ${theme.spacing[3]};

  @media (max-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

const NavList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: row;
    gap: ${theme.spacing[2]};
    flex-wrap: nowrap;
  }
`;

const NavRow = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  width: 100%;
  background: ${({ $active }) => ($active ? theme.colors.primary[50] : 'transparent')};
  border: 1px solid ${({ $active }) => ($active ? theme.colors.primary[200] : 'transparent')};
  border-radius: ${theme.borderRadius.md};
  color: ${({ $active }) => ($active ? theme.colors.textPrimary : theme.colors.textSecondary)};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  text-align: left;
  cursor: pointer;
  position: relative;
  transition: background ${theme.transitions.base}, color ${theme.transitions.base},
    border-color ${theme.transitions.base};

  &:hover {
    background: ${({ $active }) => ($active ? theme.colors.primary[50] : theme.colors.surface)};
    color: ${theme.colors.textPrimary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}33;
  }

  /* Active-state left-bar accent. */
  ${({ $active }) =>
    $active &&
    css`
      &::before {
        content: '';
        position: absolute;
        left: -${theme.spacing[4]};
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 22px;
        background: ${theme.colors.primary[500]};
        border-radius: 0 ${theme.borderRadius.full} ${theme.borderRadius.full} 0;

        @media (max-width: ${theme.breakpoints.md}) {
          left: 50%;
          top: auto;
          bottom: -${theme.spacing[3]};
          transform: translateX(-50%);
          width: 22px;
          height: 3px;
          border-radius: ${theme.borderRadius.full} ${theme.borderRadius.full} 0 0;
        }
      }
    `}

  @media (max-width: ${theme.breakpoints.md}) {
    flex-shrink: 0;
    width: auto;
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
  }
`;

const NavIcon = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  background: ${({ $active }) => ($active ? theme.colors.primary[500] : theme.colors.surface)};
  color: ${({ $active }) => ($active ? '#fff' : theme.colors.primary[500])};
  border: 1px solid ${({ $active }) => ($active ? theme.colors.primary[600] : theme.colors.borderLight)};
  flex-shrink: 0;
  transition: background ${theme.transitions.base}, color ${theme.transitions.base};
  box-shadow: ${({ $active }) => ($active ? '0 4px 10px -4px rgba(0, 0, 41, 0.45)' : 'none')};
`;

const NavBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;

const NavLabel = styled.span`
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  letter-spacing: -0.01em;
`;

const NavMeta = styled.span`
  font-size: 11px;
  color: ${theme.colors.textMuted};
  font-weight: ${theme.typography.fontWeight.normal};

  @media (max-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

const NavSoonChip = styled.span`
  font-size: 9px;
  letter-spacing: 0.12em;
  font-weight: ${theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  padding: 3px ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.backgroundTertiary};
  flex-shrink: 0;

  @media (max-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

// =============================================================================
// CONTENT (right column)
// =============================================================================

const ContentArea = styled.div`
  padding: ${theme.spacing[8]};
  animation: ${tabSwap} 0.25s ease-out both;

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[6]} ${theme.spacing[5]};
  }
`;

const ContentEyebrow = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${theme.colors.primary[500]};
  margin-bottom: ${theme.spacing[2]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & .num {
    color: ${theme.colors.textMuted};
    font-variant-numeric: tabular-nums;
  }
`;

const ContentHeading = styled.h2`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[1]};
  letter-spacing: -0.02em;
`;

const ContentDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0 0 ${theme.spacing[8]};
  max-width: 580px;
`;

// Numbered sub-section for groups within a tab (e.g. "01 · DISPLAY", "02 · LOCALE").
const SubSection = styled.div<{ $first?: boolean }>`
  ${({ $first }) =>
    $first
      ? css`
          padding-top: 0;
        `
      : css`
          margin-top: ${theme.spacing[8]};
          padding-top: ${theme.spacing[8]};
          border-top: 1px solid ${theme.colors.borderLight};
        `}
`;

const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${theme.colors.primary[500]};
  margin-bottom: ${theme.spacing[2]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & .num {
    color: ${theme.colors.textMuted};
    font-variant-numeric: tabular-nums;
  }
`;

const SubHeading = styled.h3`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[1]};
  letter-spacing: -0.015em;
`;

const SubHelper = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0 0 ${theme.spacing[5]};
  max-width: 540px;
`;

// =============================================================================
// SETTING ROW
// =============================================================================

const SettingList = styled.div`
  display: flex;
  flex-direction: column;
`;

const SettingRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[4]} 0;
  border-bottom: 1px dashed ${theme.colors.borderLight};
  position: relative;
  transition: padding ${theme.transitions.base};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    padding-left: ${theme.spacing[2]};
  }

  &:hover::before {
    opacity: 1;
  }

  &::before {
    content: '';
    position: absolute;
    left: -${theme.spacing[3]};
    top: ${theme.spacing[4]};
    bottom: ${theme.spacing[4]};
    width: 2px;
    background: ${theme.colors.primary[500]};
    border-radius: ${theme.borderRadius.full};
    opacity: 0;
    transition: opacity ${theme.transitions.base};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-wrap: wrap;
  }
`;

const RowIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.primary[50]};
  color: ${theme.colors.primary[500]};
  border: 1px solid ${theme.colors.primary[100]};
  flex-shrink: 0;
`;

const RowInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowTitle = styled.h4`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 2px;
  letter-spacing: -0.005em;
`;

const RowDesc = styled.p`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  margin: 0;
  line-height: 1.5;
`;

const RowControl = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  flex-shrink: 0;

  @media (max-width: ${theme.breakpoints.sm}) {
    width: 100%;
    margin-top: ${theme.spacing[2]};
    margin-left: 52px; /* align with row content past the icon */
  }
`;

// =============================================================================
// FORM CONTROLS
// =============================================================================

const inputBase = css`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-family: inherit;
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
  transition: all ${theme.transitions.base};
  cursor: pointer;
  min-width: 160px;

  &:hover:not(:disabled) {
    border-color: ${theme.colors.borderHover};
    background: ${theme.colors.surfaceHover};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow:
      0 0 0 3px ${theme.colors.primary[500]}1a,
      0 1px 2px rgba(0, 0, 0, 0.04);
  }

  &:disabled {
    background: ${theme.colors.backgroundTertiary};
    color: ${theme.colors.textSecondary};
    cursor: not-allowed;
    opacity: 0.85;
  }
`;

const Select = styled.select`
  ${inputBase}
  /* Custom-style the select arrow with an inline SVG so disabled doesn't
     show the browser's harsh default. */
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23696969' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
  background-repeat: no-repeat;
  background-position: right ${theme.spacing[3]} center;
  padding-right: ${theme.spacing[8]};
`;

const NumberInput = styled.input`
  ${inputBase}
  width: 110px;
  min-width: 110px;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

// =============================================================================
// TOGGLE
// =============================================================================

const Toggle = styled.button<{ $enabled?: boolean }>`
  position: relative;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
  background: ${({ $enabled }) => ($enabled ? theme.colors.primary[500] : theme.colors.borderLight)};
  border: 1px solid
    ${({ $enabled }) => ($enabled ? theme.colors.primary[600] : theme.colors.border)};
  border-radius: ${theme.borderRadius.full};
  cursor: pointer;
  padding: 0;
  transition: background-color ${theme.transitions.base}, border-color ${theme.transitions.base};

  &::after {
    content: '';
    position: absolute;
    top: 1px;
    left: ${({ $enabled }) => ($enabled ? '21px' : '1px')};
    width: 20px;
    height: 20px;
    background: #fff;
    border-radius: 50%;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.08);
    transition: left ${theme.transitions.base};
  }

  &:hover:not(:disabled) {
    background: ${({ $enabled }) =>
      $enabled ? theme.colors.primary[700] : theme.colors.borderHover};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}33;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

// =============================================================================
// PREVIEW BANNER (per-tab inline note explaining the disabled state)
// =============================================================================

const InlineNote = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.primary[50]};
  border: 1px solid ${theme.colors.primary[100]};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing[6]};

  & .icon {
    width: 28px;
    height: 28px;
    border-radius: ${theme.borderRadius.sm};
    background: ${theme.colors.surface};
    color: ${theme.colors.primary[500]};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border: 1px solid ${theme.colors.primary[100]};
  }

  & .copy {
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.textSecondary};
    line-height: 1.5;
    flex: 1;
    min-width: 0;
  }

  & strong {
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
  }
`;

// =============================================================================
// STICKY ACTION BAR
// =============================================================================

const ActionBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 5;
  padding: ${theme.spacing[4]} ${theme.spacing[8]};
  border-top: 1px solid ${theme.colors.borderLight};
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  border-radius: 0 0 ${theme.borderRadius.xl} ${theme.borderRadius.xl};
  box-shadow: 0 -8px 24px -16px rgba(0, 0, 41, 0.12);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[3]} ${theme.spacing[5]};
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

const ActionLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const ActionRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${theme.colors.warning[500]};
  flex-shrink: 0;
`;

const StickyMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  min-width: 0;
`;

const StatusMessage = styled.div<{ $type: 'success' | 'error' }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 6px ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};

  ${({ $type }) =>
    $type === 'success'
      ? css`
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[700]};
        `
      : css`
          background: ${theme.colors.error[100]};
          color: ${theme.colors.error[700]};
        `}
`;

const PrimaryButton = styled.button<{ $loading?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: #fff;
  border: 1px solid ${theme.colors.primary[600]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: ${({ $loading }) => ($loading ? 'wait' : 'pointer')};
  opacity: ${({ $loading }) => ($loading ? 0.85 : 1)};
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    background ${theme.transitions.base};
  box-shadow: 0 6px 14px -6px rgba(0, 0, 41, 0.45);

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[700]};
    transform: translateY(-1px);
    box-shadow: 0 10px 18px -6px rgba(0, 0, 41, 0.55);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 4px 10px -6px rgba(0, 0, 41, 0.45);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
    box-shadow: none;
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: transparent;
  color: ${theme.colors.textPrimary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover:not(:disabled) {
    background: ${theme.colors.backgroundSecondary};
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[700]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}33;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ButtonSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

// =============================================================================
// LOADING
// =============================================================================

const LoadingShell = styled.div`
  padding: ${theme.spacing[16]} ${theme.spacing[6]};
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

const LoadingSpinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid ${theme.colors.borderLight};
  border-top-color: ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

// =============================================================================
// TYPES (preserved as-is)
// =============================================================================

interface SettingsData {
  ui_preferences?: {
    theme: string;
    language: string;
    timezone: string;
    date_format: string;
    time_format: string;
    dashboard_layout: string;
  };
  notification_settings?: {
    email_notifications: {
      analysis_complete: boolean;
      project_updates: boolean;
      system_announcements: boolean;
      weekly_digest: boolean;
    };
    push_notifications: {
      browser_notifications: boolean;
      analysis_updates: boolean;
      system_alerts: boolean;
    };
  };
  workflow_preferences?: {
    auto_save_interval: number;
    default_analysis_type: string;
    concurrent_analyses: number;
    export_format: string;
  };
  privacy_settings?: {
    profile_visibility: string;
    activity_tracking: boolean;
    data_sharing: boolean;
    session_timeout: number;
  };
  [key: string]: any;
}

// =============================================================================
// COMPONENT
// =============================================================================

const SettingsPage: React.FC = () => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState('ui');
  const [settingsData, setSettingsData] = useState<SettingsData>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await AnalysisAPI.getUserSettings();
      if (response.success) {
        setSettingsData(response.data);
      } else {
        showToast({ type: 'error', title: response.error || 'Failed to load settings' });
      }
    } catch (error) {
      showToast({ type: 'error', title: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (category: string, field: string, value: any) => {
    setSettingsData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value,
      },
    }));

    if (status) {
      setStatus(null);
    }
  };

  const updateNestedSetting = (category: string, subCategory: string, field: string, value: any) => {
    setSettingsData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [subCategory]: {
          ...prev[category]?.[subCategory],
          [field]: value,
        },
      },
    }));

    if (status) {
      setStatus(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);

    try {
      const response = await AnalysisAPI.updateUserSettings(settingsData);

      if (response.success) {
        setStatus({ type: 'success', message: 'Settings saved successfully!' });
        showToast({ type: 'success', title: 'Settings saved successfully' });
        await loadSettings();
      } else {
        setStatus({ type: 'error', message: response.error || 'Failed to save settings' });
        showToast({ type: 'error', title: response.error || 'Failed to save settings' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Failed to save settings' });
      showToast({ type: 'error', title: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    setStatus(null);

    try {
      const response = await AnalysisAPI.resetUserSettings();

      if (response.success) {
        setStatus({ type: 'success', message: 'Settings reset to defaults!' });
        showToast({ type: 'success', title: 'Settings reset to defaults' });
        await loadSettings();
      } else {
        setStatus({ type: 'error', message: response.error || 'Failed to reset settings' });
        showToast({ type: 'error', title: response.error || 'Failed to reset settings' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Failed to reset settings' });
      showToast({ type: 'error', title: 'Failed to reset settings' });
    } finally {
      setResetting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Tab metadata. The "meta" line surfaces a count or short hint; "soon" is the
  // ghost chip per the design brief — every tab's controls are disabled until
  // the sync layer ships, so each carries the same status.
  // ---------------------------------------------------------------------------
  const tabs: Array<{
    id: string;
    label: string;
    meta: string;
    icon: React.ReactNode;
  }> = [
    { id: 'ui', label: 'Interface', meta: 'Display & locale', icon: <Monitor size={16} /> },
    { id: 'notifications', label: 'Notifications', meta: 'Email & push', icon: <Bell size={16} /> },
    { id: 'workflow', label: 'Workflow', meta: 'Automation & defaults', icon: <Zap size={16} /> },
    { id: 'privacy', label: 'Privacy', meta: 'Visibility & data', icon: <Shield size={16} /> },
  ];

  // ---------------------------------------------------------------------------
  // LOADING STATE
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <SettingsContainer>
        <Particles />
        <Container>
          <ContentWrapper>
            <LoadingShell>
              <LoadingSpinner />
              Loading your settings…
            </LoadingShell>
          </ContentWrapper>
        </Container>
      </SettingsContainer>
    );
  }

  // ---------------------------------------------------------------------------
  // CONTENT — INTERFACE TAB
  // ---------------------------------------------------------------------------
  const renderInterface = () => (
    <>
      <ContentEyebrow>
        <span className="num">01</span>
        <span>·</span>
        <span>Interface</span>
      </ContentEyebrow>
      <ContentHeading>Display &amp; localisation</ContentHeading>
      <ContentDescription>
        Choose how the app looks and how dates, times, and numbers are presented across your account.
      </ContentDescription>

      <InlineNote>
        <span className="icon">
          <Eye size={14} />
        </span>
        <span className="copy">
          <strong>Browse-only.</strong> Your selections will persist as soon as the preferences API ships.
          Until then, the app falls back to its default display settings.
        </span>
      </InlineNote>

      <SubSection $first>
        <Eyebrow>
          <span className="num">01</span>
          <span>·</span>
          <span>Display</span>
        </Eyebrow>
        <SubHeading>Theme &amp; appearance</SubHeading>
        <SubHelper>Light or dark mode, plus how the app should follow your system.</SubHelper>

        <SettingList>
          <SettingRow>
            <RowIcon>
              <Palette size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Theme</RowTitle>
              <RowDesc>Choose your preferred colour theme.</RowDesc>
            </RowInfo>
            <RowControl>
              <Select value={settingsData.ui_preferences?.theme || 'light'} disabled>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="auto">Auto</option>
              </Select>
            </RowControl>
          </SettingRow>
        </SettingList>
      </SubSection>

      <SubSection>
        <Eyebrow>
          <span className="num">02</span>
          <span>·</span>
          <span>Locale</span>
        </Eyebrow>
        <SubHeading>Language &amp; formats</SubHeading>
        <SubHelper>Used for the app's UI strings, date and time formats, and currency hints.</SubHelper>

        <SettingList>
          <SettingRow>
            <RowIcon>
              <Globe size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Language</RowTitle>
              <RowDesc>Select your preferred language for the interface.</RowDesc>
            </RowInfo>
            <RowControl>
              <Select value={settingsData.ui_preferences?.language || 'en'} disabled>
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
              </Select>
            </RowControl>
          </SettingRow>

          <SettingRow>
            <RowIcon>
              <Calendar size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Date format</RowTitle>
              <RowDesc>Choose how dates appear in tables, exports, and timestamps.</RowDesc>
            </RowInfo>
            <RowControl>
              <Select value={settingsData.ui_preferences?.date_format || 'MM/DD/YYYY'} disabled>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </Select>
            </RowControl>
          </SettingRow>

          <SettingRow>
            <RowIcon>
              <Clock size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Time format</RowTitle>
              <RowDesc>12-hour with AM/PM or 24-hour clock.</RowDesc>
            </RowInfo>
            <RowControl>
              <Select value={settingsData.ui_preferences?.time_format || '12h'} disabled>
                <option value="12h">12-hour</option>
                <option value="24h">24-hour</option>
              </Select>
            </RowControl>
          </SettingRow>
        </SettingList>
      </SubSection>
    </>
  );

  // ---------------------------------------------------------------------------
  // CONTENT — NOTIFICATIONS TAB
  // ---------------------------------------------------------------------------
  const renderNotifications = () => (
    <>
      <ContentEyebrow>
        <span className="num">02</span>
        <span>·</span>
        <span>Notifications</span>
      </ContentEyebrow>
      <ContentHeading>What we ping you about</ContentHeading>
      <ContentDescription>
        Pick which events should reach your inbox or surface as a browser notification.
      </ContentDescription>

      <InlineNote>
        <span className="icon">
          <BellOff size={14} />
        </span>
        <span className="copy">
          <strong>All notifications are paused</strong> while the preferences API ships. You won't receive
          email or push alerts regardless of the toggle state shown here.
        </span>
      </InlineNote>

      <SubSection $first>
        <Eyebrow>
          <span className="num">01</span>
          <span>·</span>
          <span>Email</span>
        </Eyebrow>
        <SubHeading>Email notifications</SubHeading>
        <SubHelper>Sent to the address you signed in with — change it on your profile.</SubHelper>

        <SettingList>
          <SettingRow>
            <RowIcon>
              <Mail size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Analysis complete</RowTitle>
              <RowDesc>Get an email when a tender analysis finishes processing.</RowDesc>
            </RowInfo>
            <RowControl>
              <Toggle
                $enabled={
                  settingsData.notification_settings?.email_notifications?.analysis_complete ?? true
                }
                disabled
                aria-label="Toggle analysis-complete email"
              />
            </RowControl>
          </SettingRow>

          <SettingRow>
            <RowIcon>
              <Mail size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Project updates</RowTitle>
              <RowDesc>Notifications when collaborators change or comment on a project.</RowDesc>
            </RowInfo>
            <RowControl>
              <Toggle
                $enabled={
                  settingsData.notification_settings?.email_notifications?.project_updates ?? true
                }
                disabled
                aria-label="Toggle project-updates email"
              />
            </RowControl>
          </SettingRow>

          <SettingRow>
            <RowIcon>
              <Calendar size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Weekly digest</RowTitle>
              <RowDesc>A Monday-morning summary of activity, wins, and pending bids.</RowDesc>
            </RowInfo>
            <RowControl>
              <Toggle
                $enabled={
                  settingsData.notification_settings?.email_notifications?.weekly_digest ?? false
                }
                disabled
                aria-label="Toggle weekly-digest email"
              />
            </RowControl>
          </SettingRow>
        </SettingList>
      </SubSection>

      <SubSection>
        <Eyebrow>
          <span className="num">02</span>
          <span>·</span>
          <span>Push</span>
        </Eyebrow>
        <SubHeading>Browser notifications</SubHeading>
        <SubHelper>
          Real-time toasts and system alerts. Requires browser permission once the feature ships.
        </SubHelper>

        <SettingList>
          <SettingRow>
            <RowIcon>
              <MessageSquare size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Browser notifications</RowTitle>
              <RowDesc>Allow OpenTender to deliver notifications in your browser.</RowDesc>
            </RowInfo>
            <RowControl>
              <Toggle
                $enabled={
                  settingsData.notification_settings?.push_notifications?.browser_notifications ?? true
                }
                disabled
                aria-label="Toggle browser notifications"
              />
            </RowControl>
          </SettingRow>

          <SettingRow>
            <RowIcon>
              <Zap size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Analysis updates</RowTitle>
              <RowDesc>Live progress updates while a tender analysis is running.</RowDesc>
            </RowInfo>
            <RowControl>
              <Toggle
                $enabled={
                  settingsData.notification_settings?.push_notifications?.analysis_updates ?? true
                }
                disabled
                aria-label="Toggle analysis-updates push"
              />
            </RowControl>
          </SettingRow>
        </SettingList>
      </SubSection>
    </>
  );

  // ---------------------------------------------------------------------------
  // CONTENT — WORKFLOW TAB
  // ---------------------------------------------------------------------------
  const renderWorkflow = () => (
    <>
      <ContentEyebrow>
        <span className="num">03</span>
        <span>·</span>
        <span>Workflow</span>
      </ContentEyebrow>
      <ContentHeading>How you work with OpenTender</ContentHeading>
      <ContentDescription>
        Defaults that drive new analyses, exports, and how often the app saves your progress.
      </ContentDescription>

      <InlineNote>
        <span className="icon">
          <Settings size={14} />
        </span>
        <span className="copy">
          <strong>Defaults remain in force.</strong> The app currently auto-saves every 2 minutes, runs up to
          three concurrent analyses, and exports as PDF. You can browse alternatives here.
        </span>
      </InlineNote>

      <SubSection $first>
        <Eyebrow>
          <span className="num">01</span>
          <span>·</span>
          <span>Automation</span>
        </Eyebrow>
        <SubHeading>Auto-save &amp; concurrency</SubHeading>
        <SubHelper>How aggressively the app commits your work and how many analyses run in parallel.</SubHelper>

        <SettingList>
          <SettingRow>
            <RowIcon>
              <Timer size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Auto-save interval</RowTitle>
              <RowDesc>How often, in seconds, your work is committed to the backend.</RowDesc>
            </RowInfo>
            <RowControl>
              <NumberInput
                type="number"
                min="30"
                max="600"
                value={settingsData.workflow_preferences?.auto_save_interval || 120}
                disabled
              />
            </RowControl>
          </SettingRow>

          <SettingRow>
            <RowIcon>
              <Users size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Concurrent analyses</RowTitle>
              <RowDesc>The maximum number of tender analyses to run at the same time.</RowDesc>
            </RowInfo>
            <RowControl>
              <NumberInput
                type="number"
                min="1"
                max="10"
                value={settingsData.workflow_preferences?.concurrent_analyses || 3}
                disabled
              />
            </RowControl>
          </SettingRow>
        </SettingList>
      </SubSection>

      <SubSection>
        <Eyebrow>
          <span className="num">02</span>
          <span>·</span>
          <span>Defaults</span>
        </Eyebrow>
        <SubHeading>New analysis &amp; export</SubHeading>
        <SubHelper>What kind of analysis to run by default and how to ship the resulting reports.</SubHelper>

        <SettingList>
          <SettingRow>
            <RowIcon>
              <Zap size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Default analysis type</RowTitle>
              <RowDesc>The mode used when starting a new project from scratch.</RowDesc>
            </RowInfo>
            <RowControl>
              <Select
                value={settingsData.workflow_preferences?.default_analysis_type || 'full'}
                disabled
              >
                <option value="full">Full analysis</option>
                <option value="quick">Quick analysis</option>
                <option value="custom">Custom</option>
              </Select>
            </RowControl>
          </SettingRow>

          <SettingRow>
            <RowIcon>
              <Download size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Export format</RowTitle>
              <RowDesc>Preferred format when exporting reports and final responses.</RowDesc>
            </RowInfo>
            <RowControl>
              <Select value={settingsData.workflow_preferences?.export_format || 'pdf'} disabled>
                <option value="pdf">PDF</option>
                <option value="docx">Word document</option>
                <option value="xlsx">Excel</option>
                <option value="json">JSON</option>
              </Select>
            </RowControl>
          </SettingRow>
        </SettingList>
      </SubSection>
    </>
  );

  // ---------------------------------------------------------------------------
  // CONTENT — PRIVACY TAB
  // ---------------------------------------------------------------------------
  const renderPrivacy = () => (
    <>
      <ContentEyebrow>
        <span className="num">04</span>
        <span>·</span>
        <span>Privacy</span>
      </ContentEyebrow>
      <ContentHeading>Visibility &amp; data controls</ContentHeading>
      <ContentDescription>
        Decide who can see your profile, how the app uses telemetry, and how long sessions live.
      </ContentDescription>

      <InlineNote>
        <span className="icon">
          <Shield size={14} />
        </span>
        <span className="copy">
          <strong>Defaults are conservative.</strong> Visibility is restricted to your organisation,
          activity tracking is internal-only, and data sharing is off until you opt in.
        </span>
      </InlineNote>

      <SubSection $first>
        <Eyebrow>
          <span className="num">01</span>
          <span>·</span>
          <span>Visibility</span>
        </Eyebrow>
        <SubHeading>Who can see your profile</SubHeading>
        <SubHelper>Affects the avatar &amp; identity that appears next to your activity in shared workspaces.</SubHelper>

        <SettingList>
          <SettingRow>
            <RowIcon>
              <Eye size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Profile visibility</RowTitle>
              <RowDesc>Public, restricted to your organisation, or fully private.</RowDesc>
            </RowInfo>
            <RowControl>
              <Select
                value={settingsData.privacy_settings?.profile_visibility || 'organization'}
                disabled
              >
                <option value="public">Public</option>
                <option value="organization">Organisation only</option>
                <option value="private">Private</option>
              </Select>
            </RowControl>
          </SettingRow>
        </SettingList>
      </SubSection>

      <SubSection>
        <Eyebrow>
          <span className="num">02</span>
          <span>·</span>
          <span>Telemetry</span>
        </Eyebrow>
        <SubHeading>How we use your activity</SubHeading>
        <SubHelper>Internal analytics and optional anonymised research data.</SubHelper>

        <SettingList>
          <SettingRow>
            <RowIcon>
              <Monitor size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Activity tracking</RowTitle>
              <RowDesc>Allow internal analytics to track your in-app actions for product improvements.</RowDesc>
            </RowInfo>
            <RowControl>
              <Toggle
                $enabled={settingsData.privacy_settings?.activity_tracking ?? true}
                disabled
                aria-label="Toggle activity tracking"
              />
            </RowControl>
          </SettingRow>

          <SettingRow>
            <RowIcon>
              <Globe size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Data sharing for research</RowTitle>
              <RowDesc>Contribute fully anonymised query patterns to public-sector research.</RowDesc>
            </RowInfo>
            <RowControl>
              <Toggle
                $enabled={settingsData.privacy_settings?.data_sharing ?? false}
                disabled
                aria-label="Toggle research data sharing"
              />
            </RowControl>
          </SettingRow>
        </SettingList>
      </SubSection>

      <SubSection>
        <Eyebrow>
          <span className="num">03</span>
          <span>·</span>
          <span>Sessions</span>
        </Eyebrow>
        <SubHeading>Session timeout</SubHeading>
        <SubHelper>How long the app stays signed in before requiring re-authentication.</SubHelper>

        <SettingList>
          <SettingRow>
            <RowIcon>
              <EyeOff size={16} />
            </RowIcon>
            <RowInfo>
              <RowTitle>Inactivity timeout</RowTitle>
              <RowDesc>Sign out automatically after this many minutes of no activity.</RowDesc>
            </RowInfo>
            <RowControl>
              <Select value={settingsData.privacy_settings?.session_timeout || 60} disabled>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={120}>2 hours</option>
                <option value={480}>8 hours</option>
              </Select>
            </RowControl>
          </SettingRow>
        </SettingList>
      </SubSection>
    </>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'ui':
        return renderInterface();
      case 'notifications':
        return renderNotifications();
      case 'workflow':
        return renderWorkflow();
      case 'privacy':
        return renderPrivacy();
      default:
        return renderInterface();
    }
  };

  return (
    <SettingsContainer>
      <Particles />
      <Container>
        <ContentWrapper>
          <PageHeader>
            <HeaderLeft>
              <Crumb>Settings</Crumb>
              <Title>Preferences</Title>
              <Subtitle>
                Customise how OpenTender looks, what it pings you about, and how it handles your work.
              </Subtitle>
            </HeaderLeft>
            <PreviewPill aria-label="Preview mode — controls activate when the sync layer ships">
              <span className="pulse" />
              <span>
                <strong>Preview mode</strong> · controls coming soon
              </span>
            </PreviewPill>
          </PageHeader>

          <SettingsCard>
            <PreviewRibbon>
              <span className="icon">
                <Clock size={14} />
              </span>
              <span className="copy">
                <strong>Settings preview</strong>{' '}
                <span>— browse your future configuration. Saves activate once the sync layer ships.</span>
              </span>
              <span className="badge">Soon</span>
            </PreviewRibbon>

            <CardBody>
              <SidebarNav aria-label="Settings sections">
                <NavEyebrow>Sections</NavEyebrow>
                <NavList>
                  {tabs.map(tab => (
                    <NavRow
                      key={tab.id}
                      type="button"
                      $active={activeTab === tab.id}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      <NavIcon $active={activeTab === tab.id}>{tab.icon}</NavIcon>
                      <NavBody>
                        <NavLabel>{tab.label}</NavLabel>
                        <NavMeta>{tab.meta}</NavMeta>
                      </NavBody>
                      <NavSoonChip>Soon</NavSoonChip>
                    </NavRow>
                  ))}
                </NavList>
              </SidebarNav>

              <ContentArea key={activeTab}>{renderActiveTab()}</ContentArea>
            </CardBody>

            <ActionBar>
              <ActionLeft>
                <SecondaryButton
                  type="button"
                  onClick={handleReset}
                  disabled
                  aria-label="Reset to defaults (disabled)"
                >
                  <RefreshCw size={14} />
                  Reset to defaults
                </SecondaryButton>
              </ActionLeft>

              <ActionRight>
                {status ? (
                  <StatusMessage $type={status.type}>
                    {status.type === 'success' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                    {status.message}
                  </StatusMessage>
                ) : (
                  <StickyMeta>
                    <Dot />
                    Preview only — saves are disabled.
                  </StickyMeta>
                )}
                <PrimaryButton
                  type="button"
                  onClick={handleSave}
                  $loading={saving}
                  disabled
                  aria-label="Save settings (disabled)"
                >
                  {saving || resetting ? <ButtonSpinner /> : <Save size={14} />}
                  {saving ? 'Saving…' : 'Save settings'}
                </PrimaryButton>
              </ActionRight>
            </ActionBar>
          </SettingsCard>
        </ContentWrapper>
      </Container>
    </SettingsContainer>
  );
};

export default SettingsPage;
